"use client";

import React, { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { Blog } from "@/lib/@types/api/blog";
import { Link } from "@/navigation.ts";
import { defaultLocale, locales } from "@/config";

// 延迟加载 BlogSearch，非首屏关键组件
const BlogSearch = dynamic(() => import("@/components/Blogs/BlogSeach.tsx"), {
	loading: () => <div className="h-12 w-full animate-pulse rounded-lg bg-gray-100" />,
});

// 内联 SVG 图标替代 antd ProfileOutlined
const ProfileIcon = () => (
	<svg className="h-6 w-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
		<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
	</svg>
);

// 文章样式定义 - 使用 CSS Module 或全局 CSS 代替 styled-components 以提升性能
// 样式已移至 src/styles/blog.scss 中的 .blog-article-content 类

// 兼容性导出：StyledArticle 组件，供其他页面使用
// 使用 CSS 类 blog-article-content 代替原来的 styled-components
export const StyledArticle = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
	<article className={`blog-article-content ${className}`}>{children}</article>
);

// 定义标题项类型
interface HeadingItem {
	text: string;
	level: number;
	id: string;
	number: string;
}

// 生成层级序号的函数
const generateHierarchicalNumbers = (
	headings: { text: string; level: number; id: string }[],
): HeadingItem[] => {
	const counters = [0, 0, 0, 0, 0]; // 对应 h1, h2, h3, h4, h5 的计数器

	return headings.map((heading) => {
		const levelIndex = heading.level - 1; // h1=0, h2=1, h3=2, h4=3, h5=4

		// 增加当前层级的计数器
		counters[levelIndex]++;

		// 重置更深层级的计数器
		for (let i = levelIndex + 1; i < counters.length; i++) {
			counters[i] = 0;
		}

		// 生成序号字符串
		const numberParts = counters.slice(0, levelIndex + 1).filter((count) => count > 0);
		const number = numberParts.join(".");

		return {
			...heading,
			number,
		};
	});
};

// 精简的博客数据类型，只包含 RelatedBlog 需要的字段
interface SimpleBlogItem {
	blog_id: number;
	blog_slug: string;
	blog_title: string;
	blog_classification_list?: { cls_slug: string; cls_name: string }[];
}

const getLocalizedArticleHref = (href: string, locale: string, siteOrigin: string): string => {
	const trimmedHref = href.trim();
	const isPageAnchor = trimmedHref.startsWith("#");
	const isProtocolRelativeUrl = trimmedHref.startsWith("//");
	const isRootRelativeUrl = trimmedHref.startsWith("/") && !isProtocolRelativeUrl;
	const isAbsoluteHttpUrl = /^https?:\/\//i.test(trimmedHref);

	if (!trimmedHref || isPageAnchor || (!isRootRelativeUrl && !isAbsoluteHttpUrl && !isProtocolRelativeUrl)) {
		return href;
	}

	let parsedUrl: URL;

	try {
		parsedUrl = new URL(trimmedHref, siteOrigin);
	} catch {
		return href;
	}

	if (parsedUrl.origin !== siteOrigin) {
		return href;
	}

	const pathnameSegments = parsedUrl.pathname.split("/").filter(Boolean);
	const firstPathSegment = pathnameSegments[0];

	if (firstPathSegment && locales.includes(firstPathSegment)) {
		pathnameSegments.shift();
	}

	const localePrefix = locale === defaultLocale ? "" : `/${locale}`;
	const localizedPathname = `${localePrefix}/${pathnameSegments.join("/")}`;
	const normalizedPathname = localizedPathname === "" ? "/" : localizedPathname;

	return `${normalizedPathname}${parsedUrl.search}${parsedUrl.hash}`;
};

const BlogArticle = ({ content, blogList }: { content: string; blogList: SimpleBlogItem[] }) => {
	const locale = useLocale();
	const [headings, setHeadings] = useState<HeadingItem[]>([]);
	const [newContent, setNewContent] = useState<string>(content); // 用于存储修改后的内容
	const [innerHeight, setInnerHeight] = useState<number>(1000); // 用于存储窗口高度

	// 提取文章的 H1-H5 标题并生成目录，同时更新 newContent
	useEffect(() => {
		const parser = new DOMParser();
		const doc = parser.parseFromString(content, "text/html");

		// 提取标题并生成 ID
		const foundHeadings = Array.from(doc.querySelectorAll("h1, h2, h3, h4, h5")).map((h, index) => {
			const headingText = h.textContent || "";
			const headingId = headingText.trim().toLowerCase().replace(/\s+/g, "-"); // 将空格转义为 "-"
			const level = parseInt(h.tagName.substring(1)); // 获取标题层级 (1-5)

			h.id = headingId; // 给每个标题元素设置 ID

			return {
				text: headingText,
				level: level,
				id: headingId,
			};
		});

		// dangerouslySetInnerHTML 中的链接不会经过 next-intl Link，需手动保留当前语言。
		doc.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((anchor) => {
			const href = anchor.getAttribute("href");

			if (href) {
				anchor.setAttribute("href", getLocalizedArticleHref(href, locale, window.location.origin));
			}
		});

		// 生成层级序号
		const hierarchicalHeadings = generateHierarchicalNumbers(foundHeadings);

		// 更新内容为带有 ID 的 HTML
		const updatedContent = new XMLSerializer().serializeToString(doc); // 将修改后的文档序列化为 HTML
		setNewContent(updatedContent); // 更新 newContent
		setHeadings(hierarchicalHeadings); // 更新目录
	}, [content, locale]);

	useEffect(() => {
		setInnerHeight(window.innerHeight); // 获取当前窗口高度
	}, []);

	const t = useTranslations();
	return (
		<div className="relative flex justify-between gap-x-5 py-8 max-md:flex-wrap">
			{/* 左侧文章内容 - 使用 CSS 类代替 styled-components */}
			<article
				className="blog-article-content h-[100vh] flex-1 overflow-auto overflow-y-auto pr-[460px] max-md:pr-0"
				style={{
					scrollbarWidth: "none",
					msOverflowStyle: "none",
					minHeight: innerHeight < 1000 ? "1200px" : "auto",
				}}
			>
				<div
					className="blog-content text-base"
					dangerouslySetInnerHTML={{
						__html: newContent,
					}}
				/>
			</article>

			{/* 右侧 Table of Contents (TOC) */}
			<div className={`absolute right-0 w-[450px] max-md:hidden`}>
				{/* 目录导航 */}
				<nav
					className={`border-gray-450 my-4 rounded border bg-[#f9f9f9] p-4`}
					style={{ maxHeight: "500px", overflowY: "auto" }}
				>
					<div className="mb-4 flex items-center justify-between">
						<h3 className="text-xl font-semibold">{t("blog.Contents")}</h3>
						<ProfileIcon />
					</div>
					<ul className="space-y-2">
						{headings.map((heading, index) => (
							<li
								key={index}
								className="cursor-pointer text-sm hover:underline"
								style={{ paddingLeft: `${(heading.level - 1) * 16}px` }} // 根据层级添加缩进
							>
								<span className="mr-2 font-medium text-gray-600">{heading.number}</span>
								<a className="text-black" href={`#${heading.id}`}>
									{heading.text}
								</a>
							</li>
						))}
					</ul>
				</nav>

				{/* 搜索组件 */}
				<div className="my-4">
					<BlogSearch></BlogSearch>
				</div>

				{/* 相关文章列表 */}
				{/* <div className="my-4" style={{ maxHeight: "400px", overflowY: "auto" }}> */}
				<RelatedBlog blogList={blogList}></RelatedBlog>
				{/* </div> */}
			</div>
		</div>
	);
};

const RelatedBlog = ({ blogList }: { blogList: SimpleBlogItem[] }) => {
	const t = useTranslations();

	return (
		<>
			<h3 className={"my-4 border-b border-b-gray-400 py-4 text-xl"}>{t("blog.RelatedBlogs")}</h3>
			<ul>
				{blogList &&
					blogList.map((blog) => {
						return (
							<li key={blog.blog_id} className="border-b border-b-gray-400">
								<Link href={"/blog/" + blog.blog_slug}>
									<h3
										className="my-2 line-clamp-2 !text-lg font-bold text-black"
										style={{
											display: "-webkit-box",
											WebkitLineClamp: 2,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											textOverflow: "ellipsis",
											lineHeight: "1.4em",
											maxHeight: "2.8em",
										}}
									>
										{blog.blog_title}
									</h3>
									{blog.blog_classification_list &&
										blog.blog_classification_list.map((item, index) => {
											return (
												index === 0 && (
													<div className="py-2 text-xs uppercase text-main" key={item.cls_slug}>
														{item.cls_name}
													</div>
												)
											);
										})}
								</Link>
							</li>
						);
					})}
			</ul>
		</>
	);
};

export default BlogArticle;
